import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

const CONTRACTS = [
  { id: "A", amount: 38965, yield: 68, labelKey: "contract_tier_entry", color: "border-amber-400/40 bg-amber-400/5", activeColor: "border-amber-400 bg-amber-400/15", dot: "bg-amber-400" },
  { id: "B", amount: 50123, yield: 97, labelKey: "contract_tier_growth", color: "border-orange-400/30 bg-orange-400/5", activeColor: "border-orange-400 bg-orange-400/15", dot: "bg-orange-400" },
  { id: "C", amount: 72434, yield: 113, labelKey: "contract_tier_premium", color: "border-white/15 bg-white/5", activeColor: "border-white/50 bg-white/10", dot: "bg-white/70" },
];

export default function CalculatorSection() {
  const { t } = useTranslation();
  const [selectedContract, setSelectedContract] = useState("B");
  const [customAmount, setCustomAmount] = useState("");
  const [useCustom, setUseCustom] = useState(false);

  const contract = CONTRACTS.find((c) => c.id === selectedContract)!;

  const MIN_AMOUNT = 38965;

  const investAmount = useMemo(() => {
    if (useCustom && customAmount) {
      const v = parseFloat(customAmount.replace(/[^0-9.]/g, ""));
      if (isNaN(v)) return contract.amount;
      return Math.max(v, MIN_AMOUNT);
    }
    return contract.amount;
  }, [useCustom, customAmount, contract.amount]);

  const profit = useMemo(() => Math.round(investAmount * contract.yield / 100), [investAmount, contract.yield]);
  const total = useMemo(() => Math.round(investAmount + profit), [investAmount, profit]);

  const fmt = (n: number) => n.toLocaleString("de-DE", { maximumFractionDigits: 0 });

  return (
    <section className="relative py-16 md:py-32 bg-[#070b14]">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, rgba(255,180,0,0.35) 0%, transparent 60%)" }} />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-12">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 mb-3 md:mb-4">
            <div className="w-6 md:w-8 h-px bg-amber-400/60" />
            <span className="text-amber-400 text-[10px] md:text-xs tracking-[0.3em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {t("calc_badge")}
            </span>
            <div className="w-6 md:w-8 h-px bg-amber-400/60" />
          </div>
          <h2 className="text-2xl md:text-5xl font-black text-white mb-3 md:mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            {t("calc_title")}
          </h2>
          <p className="text-white/40 text-xs md:text-sm max-w-xl mx-auto">{t("calc_subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Left: controls */}
          <div className="space-y-4 md:space-y-6">
            {/* Contract selector */}
            <div>
              <label className="text-white/40 text-[10px] md:text-xs tracking-widest uppercase block mb-2 md:mb-3">{t("calc_select_contract")}</label>
              <div className="space-y-2 md:space-y-3">
                {CONTRACTS.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setSelectedContract(c.id); setUseCustom(false); }}
                    className={`w-full flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border rounded-lg transition-all cursor-pointer ${
                      selectedContract === c.id && !useCustom ? c.activeColor : c.color
                    }`}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full ${selectedContract === c.id && !useCustom ? c.dot : "bg-white/20"}`} />
                      <div className="text-left">
                        <div className="text-white text-xs md:text-sm font-bold" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                          {c.amount.toLocaleString("de-DE")} EUR
                        </div>
                        <div className="text-white/40 text-[10px] md:text-xs">{t(c.labelKey)}</div>
                      </div>
                    </div>
                    <div className="text-amber-400 font-black text-base md:text-lg" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                      +{c.yield}%
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom amount */}
            <div>
              <label className="text-white/40 text-[10px] md:text-xs tracking-widest uppercase block mb-2">{t("calc_custom_amount")}</label>
              <div className="relative">
                <input
                  type="number"
                  min={38965}
                  value={customAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCustomAmount(val);
                    setUseCustom(true);
                  }}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val < 38965) {
                      setCustomAmount("38965");
                    }
                  }}
                  onFocus={() => setUseCustom(true)}
                  placeholder={t("calc_custom_placeholder")}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-400/50 placeholder-white/20 pr-16"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 text-sm font-bold">EUR</span>
              </div>
              <p className="text-white/20 text-[10px] md:text-xs mt-1">{t("calc_custom_note")} · {t("calc_min_amount")}: 38,965 EUR</p>
            </div>

            {/* Payout info */}
            <div className="flex items-center gap-3 p-3 md:p-4 border border-emerald-400/20 rounded-lg bg-emerald-400/5">
              <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-emerald-400/10 border border-emerald-400/20 flex-shrink-0">
                <i className="ri-time-line text-emerald-400 text-sm" />
              </div>
              <div>
                <div className="text-emerald-400 text-[10px] md:text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  {t("calc_payout_label")}
                </div>
                <div className="text-white/40 text-[10px] md:text-xs mt-0.5">{t("calc_payout_desc")}</div>
              </div>
            </div>
          </div>

          {/* Right: result */}
          <div className="flex flex-col gap-3 md:gap-4">
            {/* Main result card */}
            <div className="p-5 md:p-8 border border-amber-400/30 rounded-lg bg-amber-400/[0.04] flex-1">
              <div className="text-center mb-5 md:mb-8">
                <div className="text-white/40 text-[10px] md:text-xs tracking-widest uppercase mb-2">{t("calc_your_investment")}</div>
                <div className="text-2xl md:text-3xl font-black text-white mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  {fmt(investAmount)} <span className="text-white/40 text-lg md:text-xl">EUR</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/20 mt-2">
                  <span className="text-amber-400 text-xs font-bold">+{contract.yield}% {t("calc_yield_label")}</span>
                </div>
              </div>

              {/* Breakdown */}
              <div className="space-y-2 md:space-y-4 mb-5 md:mb-8">
                <div className="flex justify-between items-center py-2 md:py-3 border-b border-white/5">
                  <span className="text-white/40 text-xs md:text-sm">{t("calc_initial")}</span>
                  <span className="text-white font-semibold text-xs md:text-sm">{fmt(investAmount)} EUR</span>
                </div>
                <div className="flex justify-between items-center py-2 md:py-3 border-b border-white/5">
                  <span className="text-white/40 text-xs md:text-sm">{t("calc_profit")}</span>
                  <span className="text-emerald-400 font-bold text-xs md:text-sm">+{fmt(profit)} EUR</span>
                </div>
                <div className="flex justify-between items-center py-2 md:py-3">
                  <span className="text-white font-bold text-xs md:text-sm">{t("calc_total_payout")}</span>
                  <span className="text-amber-400 font-black text-base md:text-xl" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                    {fmt(total)} EUR
                  </span>
                </div>
              </div>

              {/* Visual bar */}
              <div className="mb-4 md:mb-6">
                <div className="flex justify-between text-[10px] md:text-xs text-white/30 mb-2">
                  <span>{t("calc_initial")}</span>
                  <span>{t("calc_profit")}</span>
                </div>
                <div className="h-3 md:h-4 bg-white/10 rounded-full overflow-hidden flex">
                  <div
                    className="h-full bg-white/30 rounded-l-full transition-all duration-500"
                    style={{ width: `${(investAmount / total) * 100}%` }}
                  />
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-r-full transition-all duration-500"
                    style={{ width: `${(profit / total) * 100}%` }}
                  />
                </div>
              </div>

              <div className="text-center text-white/20 text-[10px] md:text-xs">{t("calc_days_note")}</div>
            </div>

            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {[
                { label: t("calc_stat_days"), value: "3" },
                { label: t("calc_stat_yield"), value: `${contract.yield}%` },
                { label: t("calc_stat_currency"), value: "EUR" },
              ].map((s) => (
                <div key={s.label} className="p-3 md:p-4 border border-white/10 rounded-lg bg-white/[0.02] text-center">
                  <div className="text-amber-400 font-black text-base md:text-lg mb-0.5 md:mb-1" style={{ fontFamily: "'Orbitron', sans-serif" }}>{s.value}</div>
                  <div className="text-white/30 text-[9px] md:text-xs leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
